import { createRouter, createWebHistory, createMemoryHistory } from 'vue-router'
import { CONTENT_PAGE_SECTIONS } from './contentPageSections.js'

const routes = [
  { path: '/', name: 'Home', component: () => import('../views/Home.vue') },
  { path: '/what-we-do', name: 'WhatWeDo', component: () => import('../views/WhatWeDo.vue') },
  { path: '/our-approach', name: 'OurApproach', component: () => import('../views/OurApproach.vue') },
  { path: '/our-work', name: 'OurWork', component: () => import('../views/OurWork.vue') },
  { path: '/our-work/:slug', name: 'OurWorkDetail', component: () => import('../views/OurWorkDetail.vue') },
  { path: '/the-academy', name: 'TheAcademy', component: () => import('../views/TheAcademy.vue') },
  // /the-academy/{programs,alumni-network} are now generated content-page routes
  // (see CONTENT_PAGE_SECTIONS below) rendering src/content/pages/the-academy/*.md
  // Static resource sub-routes declared before /:slug to take priority
  { path: '/resources/training', name: 'Training', component: () => import('../views/Training.vue') },
  { path: '/resources/training/:slug', name: 'TrainingDetail', component: () => import('../views/TrainingDetail.vue') },
  { path: '/resources', name: 'ResourceCentre', component: () => import('../views/ResourceCentre.vue') },
  { path: '/resources/:slug', name: 'ResourceDetail', component: () => import('../views/ResourceDetail.vue') },
  // Section landing for the "Resources and training" mega-menu — splash cards to
  // /resources and /resources/training (see resources-and-training.yml). Own path
  // segment, so it doesn't collide with the /resources/:slug catch-all above.
  { path: '/resources-and-training', name: 'ResourcesAndTraining', component: () => import('../views/ResourcesAndTraining.vue') },
  { path: '/about', name: 'AboutUs', component: () => import('../views/AboutUs.vue') },
  // /about/our-network is a bespoke landing (OurNetwork.vue + our-network.yml),
  // not a generated content page. Declared as a static route so vue-router ranks
  // it above /about/:slug (same as /about/news). The old placeholder markdown
  // page (src/content/pages/about/our-network.md) was removed with this change.
  { path: '/about/our-network', name: 'OurNetwork', component: () => import('../views/OurNetwork.vue') },
  // /about/our-story is a bespoke landing (OurStory.vue + our-story.yml), like
  // OurNetwork above. Declared static so vue-router ranks it above /about/:slug;
  // the old placeholder markdown (src/content/pages/about/our-story.md) was
  // removed with this change.
  { path: '/about/our-story', name: 'OurStory', component: () => import('../views/OurStory.vue') },
  // /about/our-people is still a generated content-page route (see
  // CONTENT_PAGE_SECTIONS below) rendering src/content/pages/about/our-people.md.
  // /about/news stays a static route — vue-router ranks it above /about/:slug.
  { path: '/about/news', name: 'NewsList', component: () => import('../views/NewsList.vue') },
  { path: '/about/news/:slug', name: 'NewsDetail', component: () => import('../views/NewsDetail.vue') },
  { path: '/contact', name: 'Contact', component: () => import('../views/Contact.vue') },
  // Post-submit landing for the contact form (FormAssembly redirects here). Static
  // path, so vite-ssg's includedRoutes prerenders it automatically.
  { path: '/contact/thank-you', name: 'ContactThankYou', component: () => import('../views/ContactThankYou.vue') },
  // Post-submit landing shared by both newsletter sign-ups (Home "Stay Informed" +
  // Academy "Keep up to date"); FormAssembly redirects here. Static path → auto-prerendered.
  { path: '/stay-informed/thank-you', name: 'StayInformedThankYou', component: () => import('../views/StayInformedThankYou.vue') },
  { path: '/donate', name: 'Donate', component: () => import('../views/Donate.vue') },
  { path: '/privacy', name: 'Privacy', component: () => import('../views/Privacy.vue') },
  ...CONTENT_PAGE_SECTIONS.map(s => ({
    path: `${s.backTo}/:slug`,
    name: `${s.key}-detail`,
    component: () => import('../views/ContentPage.vue'),
    meta: { section: s.key, backTo: s.backTo, backLabel: s.backLabel }
  })),
  ...(import.meta.env.MODE === 'cms'
    ? [
        { path: '/cms-guide', name: 'CmsGuide', component: () => import('../views/CmsGuide.vue') },
        { path: '/cms-status', name: 'CmsStatus', component: () => import('../views/CmsStatus.vue') },
        { path: '/component-gallery', name: 'ComponentGallery', component: () => import('../views/ComponentGallery.vue') },
      ]
    : []),
  // Dev-only asset review tool (jk-11) — compile-time gated so the route (and
  // its chunk) doesn't exist in a production build, unlike the CMS-mode routes
  // above which need `npm run editor` specifically; this just needs `npm run dev`.
  ...(import.meta.env.DEV
    ? [
        { path: '/dev/partner-logos', name: 'PartnerLogoReview', component: () => import('../views/PartnerLogoReview.vue') },
        // Dev-only visual comparison of ResourceCard variants (hover, grid
        // ratio, CTA style, border weight, image-fill colour) ahead of a
        // design decision — same DEV-gating pattern as partner-logos above.
        { path: '/dev/resource-card-variants', name: 'ResourceCardVariants', component: () => import('../views/ResourceCardVariants.vue') },
        // Dev-only Social Image Studio (jk-24) — pickers + canvas preview for
        // generating on-brand social share images. Same DEV-gating pattern as above.
        { path: '/dev/social-images', name: 'SocialImageStudio', component: () => import('../views/SocialImageStudio.vue') },
      ]
    : []),
]

export function createRouterInstance(isClient = false) {
  return createRouter({
    history: isClient ? createWebHistory() : createMemoryHistory(),
    routes
  })
}
